import React, { useState, useEffect } from "react";
import { topCoinDistributionCardService } from "../services/topCoinDistributionCardService";
import { toast } from "react-toastify";

function TopCoinDistributionCardAssignForm({ retailers, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    retail_business_id: "",
    prefix: "",
    card_tier: "",
    notes: "",
  });

  const [loading, setLoading] = useState(false);
  const [prefixes, setPrefixes] = useState([]);
  const [cards, setCards] = useState([]);
  const [selectedCardIds, setSelectedCardIds] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);

  useEffect(() => {
    fetchPrefixes();
  }, []);

  const fetchPrefixes = async () => {
    try {
      const response = await topCoinDistributionCardService.getAvailablePrefixes();
      if (response.status) {
        setPrefixes(response.data);
      }
    } catch (error) {
      console.error("Failed to load prefixes", error);
    }
  };

  const fetchCards = async (prefix, tier) => {
    if (!prefix) {
      setCards([]);
      setSelectedCardIds([]);
      return;
    }
    setLoadingCards(true);
    try {
      const response = await topCoinDistributionCardService.getUnassignedCards({
        prefix,
        card_tier: tier || undefined,
      });
      if (response.status) {
        setCards(response.data);
        setSelectedCardIds([]);
      }
    } catch (error) {
      console.error("Failed to load cards", error);
      toast.error("Failed to load unassigned cards");
    } finally {
      setLoadingCards(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "prefix") {
      fetchCards(value, formData.card_tier);
    } else if (name === "card_tier") {
      fetchCards(formData.prefix, value);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCardIds(cards.map((c) => c.id));
    } else {
      setSelectedCardIds([]);
    }
  };

  const handleSelectCard = (id) => {
    setSelectedCardIds((prev) =>
      prev.includes(id) ? prev.filter((cardId) => cardId !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.retail_business_id || selectedCardIds.length === 0) {
      toast.error("Please select a retail business and at least one card.");
      return;
    }
    setLoading(true);
    try {
      const response = await topCoinDistributionCardService.assignCards({
        retail_business_id: formData.retail_business_id,
        card_ids: selectedCardIds,
        notes: formData.notes,
      });
      if (response.status) {
        toast.success(response.message || "TopCoin QR cards assigned successfully.");
        onSuccess();
      } else {
        toast.error(response.message || "Failed to assign cards.");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to assign cards");
    } finally {
      setLoading(false);
    }
  };

  const isAllSelected = cards.length > 0 && selectedCardIds.length === cards.length;

  return (
    <div className="modal-overlay" style={overlayStyle}>
      <div className="modal-content" style={modalStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
          <h3 style={{ margin: 0, color: "#1e3a8a", fontSize: "20px" }}>Assign TopCoin Cards to Shops</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={{ display: "flex", gap: "15px" }}>
            <div className="form-group" style={{ ...groupStyle, flex: 1.2 }}>
              <label style={labelStyle}>Retail Business (Shop) *</label>
              <select
                name="retail_business_id"
                value={formData.retail_business_id}
                onChange={handleChange}
                required
                style={inputStyle}
              >
                <option value="">Select Retail Business</option>
                {retailers.map((r) => (
                  <option key={r.retail_business_id} value={r.retail_business_id}>
                    {r.retail_business_id} - {r.shop_details?.retail_name || `${r.first_name || ""} ${r.last_name || ""}`.trim()}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ ...groupStyle, flex: 1 }}>
              <label style={labelStyle}>Card Prefix / Batch *</label>
              <select
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
                required
                style={inputStyle}
              >
                <option value="">Select Prefix</option>
                {prefixes.map((p, idx) => (
                  <option key={`${p.prefix}-${p.card_tier}-${idx}`} value={p.prefix}>
                    {p.prefix} - {p.card_tier} ({p.top_coin_value} TC) [{p.count} available]
                  </option>
                ))}
              </select>
            </div>
          </div>

          {formData.prefix && (
            <div className="form-group" style={groupStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>AVAILABLE CARDS IN BATCH</label>
                <span style={{ fontSize: "12px", color: "#475569", fontWeight: 600 }}>
                  Selected: <strong style={{ color: "#1e3a8a" }}>{selectedCardIds.length}</strong> / {cards.length}
                </span>
              </div>

              <div style={tableContainerStyle}>
                {loadingCards ? (
                  <div style={{ padding: "20px", textAlign: "center", color: "#64748b" }}>Loading cards...</div>
                ) : (
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>#</th>
                        <th style={thStyle}>CARD NUMBER</th>
                        <th style={thStyle}>SERIAL NUMBER</th>
                        <th style={thStyle}>TIER & VALUE</th>
                        <th style={{ ...thStyle, textAlign: "center" }}>
                          Select All <br />
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cards.map((card, index) => (
                        <tr key={card.id}>
                          <td style={tdStyle}>{index + 1}</td>
                          <td style={tdStyle}>
                            <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{card.card_number}</span>
                          </td>
                          <td style={tdStyle}>
                            <span style={{ fontFamily: "monospace", color: "#475569" }}>{card.serial_number}</span>
                          </td>
                          <td style={tdStyle}>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                borderRadius: "4px",
                                fontSize: "11px",
                                fontWeight: 700,
                                backgroundColor: `${card.card_color}20`,
                                color: card.card_color,
                                border: `1px solid ${card.card_color}`,
                              }}
                            >
                              {card.card_tier} ({card.top_coin_value} TC)
                            </span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={selectedCardIds.includes(card.id)}
                              onChange={() => handleSelectCard(card.id)}
                            />
                          </td>
                        </tr>
                      ))}
                      {cards.length === 0 && (
                        <tr>
                          <td colSpan="5" style={{ ...tdStyle, textAlign: "center", color: "#64748b" }}>
                            No unassigned cards found for this prefix.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* <div className="form-group" style={groupStyle}>
            <label style={labelStyle}>Assignment Notes / Batch Ref</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="e.g. Assigned for store promotion batch #1..."
              style={{ ...inputStyle, minHeight: "60px" }}
              maxLength={65535}
            />
          </div> */}

          <div className="modal-actions" style={actionsStyle}>
            <button type="button" onClick={onClose} style={cancelBtnStyle} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              style={submitBtnStyle}
              disabled={loading || selectedCardIds.length === 0}
            >
              {loading ? "Assigning..." : `Assign ${selectedCardIds.length} Cards to Shop`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1050,
};

const modalStyle = {
  backgroundColor: "#fff",
  padding: "26px",
  borderRadius: "10px",
  width: "750px",
  maxWidth: "92%",
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
};

const formStyle = { display: "flex", flexDirection: "column", gap: "15px" };
const groupStyle = { display: "flex", flexDirection: "column", gap: "4px" };
const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#374151" };
const inputStyle = { padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", fontSize: "13px" };
const actionsStyle = { display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" };
const btnBase = { padding: "10px 18px", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "13px" };
const cancelBtnStyle = { ...btnBase, backgroundColor: "#f1f5f9", color: "#475569" };
const submitBtnStyle = { ...btnBase, backgroundColor: "#1e3a8a", color: "#fff" };

const tableContainerStyle = {
  maxHeight: "260px",
  overflowY: "auto",
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
};
const tableStyle = { width: "100%", borderCollapse: "collapse" };
const thStyle = {
  backgroundColor: "#f8fafc",
  padding: "9px 12px",
  borderBottom: "1px solid #e2e8f0",
  textAlign: "left",
  fontSize: "12px",
  fontWeight: 700,
  color: "#475569",
  position: "sticky",
  top: 0,
  zIndex: 1,
};
const tdStyle = {
  padding: "9px 12px",
  borderBottom: "1px solid #f1f5f9",
  fontSize: "13px",
};

export default TopCoinDistributionCardAssignForm;
